import type Color from 'colorjs.io';

const WCAG_THRESHOLD = 2.3;

/**
 * @returns a color that contrasts well with the background.
 *
 * @param background - the background color
 * @param _light - an optional light color to use instead of the default
 * @param _dark - an optional dark color to use instead of the default
 *
 * if you don't provide a light or dark color, the function will calculate
 * one automatically by mixing the background with white / black (recommended)
 */
export const getForegroundColor = (
  background: Color,
  _light?: Color,
  _dark?: Color,
) => {
  const lightColor =
    _light ??
    background.mix('white', 0.97, {
      space: 'oklch',
      outputSpace: 'oklch',
    });

  const darkColor =
    _dark ??
    background.mix('black', 0.7, {
      space: 'oklch',
      outputSpace: 'oklch',
    });

  // prefer light unless not enough contrast
  const contrast = background.contrast(lightColor, 'WCAG21');

  if (contrast > WCAG_THRESHOLD) {
    return lightColor;
  }

  return darkColor;
};

/**
 * @returns a string like `20 14.3% 4.1%`, which is the arguments you'd pass to `hsl()` .
 * `@shadcn/ui` uses this format for colors.
 */
export function toShadCn(color: Color) {
  // there must be a better way to do this
  const [hueStr, saturationPercentStr, lightnessPercentStr] = color
    .to('hsl')
    .toString()
    .replace('hsl(', '')
    .replace(')', '')
    .split(' ');

  const [hue, saturation, lightness] = [
    parseFloat(hueStr),
    parseFloat(saturationPercentStr.replace('%', '')),
    parseFloat(lightnessPercentStr.replace('%', '')),
  ];

  return `${Math.round(hue)} ${Math.round(saturation)}% ${Math.round(lightness)}%`;
}
